import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MessageSquare,
  Mail,
  AtSign,
  Reply,
  Search,
  Filter,
  RefreshCw,
  Eye,
  EyeOff,
  Flag,
  Archive,
  Trash2,
  Send,
  CheckCircle,
  User,
  ThumbsUp,
  ThumbsDown,
  Minus,
  Sparkles,
  MessageCircle,
  X,
} from 'lucide-react';
import { api, type SMCCInboxItem as InboxItem } from '../../../services/api';

// Platform configuration
const PLATFORM_CONFIG: Record<string, { name: string; color: string }> = {
  instagram: { name: 'Instagram', color: '#E4405F' },
  tiktok: { name: 'TikTok', color: '#000000' },
  twitter: { name: 'X (Twitter)', color: '#1DA1F2' },
  facebook: { name: 'Facebook', color: '#1877F2' },
  pinterest: { name: 'Pinterest', color: '#BD081C' },
  youtube: { name: 'YouTube', color: '#FF0000' },
  linkedin: { name: 'LinkedIn', color: '#0A66C2' },
};

type InboxItemType = 'comment' | 'dm' | 'mention' | 'reply' | 'review';
type InboxItemStatus = 'unread' | 'read' | 'in_progress' | 'replied' | 'archived' | 'spam' | 'flagged' | 'escalated';
type Sentiment = 'positive' | 'negative' | 'neutral' | 'mixed';
type Priority = 'low' | 'normal' | 'high' | 'urgent';


interface InboxStats {
  total_items: number;
  total_unread: number;
  urgent_count: number;
  high_priority_count: number;
  negative_sentiment_count: number;
  needs_response_count: number;
  avg_response_time_hours: number;
  response_rate: number;
  items_today: number;
  items_this_week: number;
}

function SentimentBadge({ sentiment, score }: { sentiment?: Sentiment; score?: number }) {
  if (!sentiment) return null;

  const config = {
    positive: { icon: ThumbsUp, color: 'text-green-600 bg-green-50', label: 'Positive' },
    negative: { icon: ThumbsDown, color: 'text-red-600 bg-red-50', label: 'Negative' },
    neutral: { icon: Minus, color: 'text-gray-600 bg-gray-50', label: 'Neutral' },
    mixed: { icon: MessageCircle, color: 'text-amber-600 bg-amber-50', label: 'Mixed' },
  };

  const { icon: Icon, color, label } = config[sentiment];

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      <Icon className="w-3 h-3" />
      {label}
      {score !== undefined && <span className="opacity-75">({(score * 100).toFixed(0)}%)</span>}
    </span>
  );
}

function PriorityBadge({ priority }: { priority: Priority }) {
  const config = {
    low: { color: 'text-gray-600 bg-gray-100', label: 'Low' },
    normal: { color: 'text-blue-600 bg-blue-50', label: 'Normal' },
    high: { color: 'text-orange-600 bg-orange-50', label: 'High' },
    urgent: { color: 'text-red-600 bg-red-50', label: 'Urgent' },
  };

  const { color, label } = config[priority];

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

function StatusBadge({ status }: { status: InboxItemStatus }) {
  const config: Record<InboxItemStatus, { color: string; label: string }> = {
    unread: { color: 'text-blue-600 bg-blue-50', label: 'Unread' },
    read: { color: 'text-gray-600 bg-gray-100', label: 'Read' },
    in_progress: { color: 'text-amber-600 bg-amber-50', label: 'In Progress' },
    replied: { color: 'text-green-600 bg-green-50', label: 'Replied' },
    archived: { color: 'text-gray-500 bg-gray-100', label: 'Archived' },
    spam: { color: 'text-red-600 bg-red-50', label: 'Spam' },
    flagged: { color: 'text-orange-600 bg-orange-50', label: 'Flagged' },
    escalated: { color: 'text-purple-600 bg-purple-50', label: 'Escalated' },
  };

  const { color, label } = config[status];

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

function ItemTypeIcon({ type }: { type: InboxItemType }) {
  const icons = {
    comment: MessageSquare,
    dm: Mail,
    mention: AtSign,
    reply: Reply,
    review: MessageCircle,
  };

  const Icon = icons[type] || MessageSquare;
  return <Icon className="w-4 h-4 text-gray-400" />;
}

function InboxItemRow({
  item,
  isSelected,
  onSelect,
  onClick,
}: {
  item: InboxItem;
  isSelected: boolean;
  onSelect: (id: string, selected: boolean) => void;
  onClick: (item: InboxItem) => void;
}) {
  const platform = item.smcc_accounts?.platform || 'unknown';
  const platformConfig = PLATFORM_CONFIG[platform];
  const isUnread = item.status === 'unread';

  return (
    <div
      className={`flex items-center gap-3 p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
        isSelected ? 'bg-primary-50' : ''
      } ${isUnread ? 'bg-blue-50/30' : ''}`}
      onClick={() => onClick(item)}
      onKeyDown={(e) => e.key === 'Enter' && onClick(item)}
      role="button"
      tabIndex={0}
    >
      {/* Checkbox */}
      <input
        type="checkbox"
        checked={isSelected}
        onChange={(e) => {
          e.stopPropagation();
          onSelect(item.id, e.target.checked);
        }}
        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
      />

      {/* Sender avatar */}
      <div className="relative flex-shrink-0">
        {item.sender_profile_image ? (
          <img
            src={item.sender_profile_image}
            alt={item.sender_username || 'User'}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
            <User className="w-5 h-5 text-gray-500" />
          </div>
        )}
        {/* Platform indicator */}
        <div
          className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white"
          style={{ backgroundColor: platformConfig?.color || '#gray' }}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`font-medium ${isUnread ? 'text-gray-900' : 'text-gray-700'}`}>
            {item.sender_display_name || item.sender_username || 'Unknown User'}
          </span>
          {item.sender_is_verified && (
            <CheckCircle className="w-3.5 h-3.5 text-blue-500 fill-blue-500" />
          )}
          <ItemTypeIcon type={item.item_type} />
          <span className="text-xs text-gray-400">{item.item_type}</span>
        </div>
        <p className={`text-sm truncate ${isUnread ? 'text-gray-700' : 'text-gray-500'}`}>
          {item.content}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <SentimentBadge sentiment={item.sentiment} />
          {item.requires_response && item.status !== 'replied' && (
            <span className="text-xs text-orange-600">Needs response</span>
          )}
        </div>
      </div>

      {/* Meta */}
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <span className="text-xs text-gray-400">
          {new Date(item.received_at).toLocaleDateString()}
        </span>
        <div className="flex items-center gap-1">
          <PriorityBadge priority={item.priority} />
          <StatusBadge status={item.status} />
        </div>
      </div>
    </div>
  );
}

function InboxDetailPanel({
  item,
  onClose,
  onReply,
  onAnalyze,
}: {
  item: InboxItem;
  onClose: () => void;
  onReply: (content: string) => void;
  onAnalyze: () => void;
}) {
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);

  const platform = item.smcc_accounts?.platform || 'unknown';
  const platformConfig = PLATFORM_CONFIG[platform];

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setIsSending(true);
    try {
      await onReply(replyText);
      setReplyText('');
    } finally {
      setIsSending(false);
    }
  };

  const handleUseSuggestion = (text: string) => {
    setReplyText(text);
  };

  return (
    <div className="flex flex-col h-full bg-white border-l border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          {item.sender_profile_image ? (
            <img
              src={item.sender_profile_image}
              alt={item.sender_username || 'User'}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
              <User className="w-5 h-5 text-gray-500" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">
                {item.sender_display_name || item.sender_username}
              </span>
              {item.sender_is_verified && (
                <CheckCircle className="w-4 h-4 text-blue-500 fill-blue-500" />
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span style={{ color: platformConfig?.color }}>{platformConfig?.name}</span>
              <span>@{item.sender_username}</span>
              {item.sender_follower_count && (
                <span>{item.sender_follower_count.toLocaleString()} followers</span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Message */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <ItemTypeIcon type={item.item_type} />
            <span className="text-xs text-gray-500 capitalize">{item.item_type}</span>
            <span className="text-xs text-gray-400">
              {new Date(item.received_at).toLocaleString()}
            </span>
          </div>
          <p className="text-gray-900 whitespace-pre-wrap">{item.content}</p>

          {item.media_urls && item.media_urls.length > 0 && (
            <div className="mt-3 flex gap-2 flex-wrap">
              {item.media_urls.map((media, idx) => (
                <img
                  key={idx}
                  src={media.url}
                  alt=""
                  className="w-20 h-20 rounded-lg object-cover"
                />
              ))}
            </div>
          )}
        </div>

        {/* Analysis */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">Analysis</h4>
            <button
              onClick={onAnalyze}
              className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
            >
              <Sparkles className="w-3 h-3" />
              Re-analyze
            </button>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Sentiment:</span>
                <SentimentBadge sentiment={item.sentiment} score={item.sentiment_score} />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Intent:</span>
                <span className="text-xs font-medium text-gray-700 capitalize">{item.intent || 'Unknown'}</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Priority:</span>
                <PriorityBadge priority={item.priority} />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Status:</span>
                <StatusBadge status={item.status} />
              </div>
            </div>
          </div>
        </div>

        {/* AI Suggestions */}
        {item.suggested_responses && item.suggested_responses.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-500" />
              AI Suggested Responses
            </h4>
            <div className="space-y-2">
              {item.suggested_responses.map((suggestion: { text: string; tone: string; confidence: number }, idx: number) => (
                <button
                  key={idx}
                  onClick={() => handleUseSuggestion(suggestion.text)}
                  className="w-full text-left p-3 bg-purple-50 border border-purple-100 rounded-lg hover:bg-purple-100 transition-colors"
                >
                  <p className="text-sm text-gray-700">{suggestion.text}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-purple-600 capitalize">{suggestion.tone}</span>
                    <span className="text-xs text-gray-400">
                      {(suggestion.confidence * 100).toFixed(0)}% confidence
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Previous response */}
        {item.response_content && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Your Response</h4>
            <div className="bg-green-50 border border-green-100 rounded-lg p-3">
              <p className="text-sm text-gray-700">{item.response_content}</p>
              <span className="text-xs text-gray-400 mt-1 block">
                Sent {item.responded_at ? new Date(item.responded_at).toLocaleString() : 'Unknown'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Reply box */}
      {item.status !== 'replied' && item.status !== 'archived' && item.status !== 'spam' && (
        <div className="p-4 border-t border-gray-200">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type your reply..."
            className="w-full p-3 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            rows={3}
          />
          <div className="flex items-center justify-between mt-2">
            <div className="text-xs text-gray-400">
              Press Ctrl+Enter to send
            </div>
            <button
              onClick={handleSendReply}
              disabled={!replyText.trim() || isSending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              {isSending ? 'Sending...' : 'Send Reply'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SMCCInbox() {
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedItem, setSelectedItem] = useState<InboxItem | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    sentiment: '',
    item_type: '',
    search: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  // Fetch inbox stats
  const { data: statsData } = useQuery({
    queryKey: ['smcc-inbox-stats'],
    queryFn: () => api.getSMCCInboxStats(),
  });

  // Fetch inbox items
  const { data: inboxData, isLoading, refetch } = useQuery({
    queryKey: ['smcc-inbox', filters],
    queryFn: () => api.getSMCCInbox({
      status: filters.status || undefined,
      priority: filters.priority || undefined,
      sentiment: filters.sentiment || undefined,
      item_type: filters.item_type || undefined,
      search: filters.search || undefined,
      limit: 50,
    }),
  });

  // Reply mutation
  const replyMutation = useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) =>
      api.replySMCCInbox(id, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-inbox'] });
      queryClient.invalidateQueries({ queryKey: ['smcc-inbox-stats'] });
    },
  });

  // Bulk action mutation
  const bulkActionMutation = useMutation({
    mutationFn: ({ ids, action }: { ids: string[]; action: string }) =>
      api.bulkUpdateSMCCInbox(ids, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-inbox'] });
      queryClient.invalidateQueries({ queryKey: ['smcc-inbox-stats'] });
      setSelectedIds(new Set());
    },
  });

  // Analyze mutation
  const analyzeMutation = useMutation({
    mutationFn: (id: string) => api.analyzeSMCCInboxItem(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['smcc-inbox'] });
      if (selectedItem && data.item) {
        setSelectedItem(data.item);
      }
    },
  });

  const stats: InboxStats = statsData?.stats || {
    total_items: 0,
    total_unread: 0,
    urgent_count: 0,
    high_priority_count: 0,
    negative_sentiment_count: 0,
    needs_response_count: 0,
    avg_response_time_hours: 0,
    response_rate: 0,
    items_today: 0,
    items_this_week: 0,
  };

  const items: InboxItem[] = inboxData?.items || [];

  const handleSelectItem = (id: string, selected: boolean) => {
    const newSelected = new Set(selectedIds);
    if (selected) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedIds(new Set(items.map((item) => item.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleBulkAction = (action: string) => {
    if (selectedIds.size === 0) return;
    bulkActionMutation.mutate({ ids: Array.from(selectedIds), action });
  };

  const handleReply = async (content: string) => {
    if (!selectedItem) return;
    await replyMutation.mutateAsync({ id: selectedItem.id, content });
    setSelectedItem(null);
  };

  const handleAnalyze = () => {
    if (!selectedItem) return;
    analyzeMutation.mutate(selectedItem.id);
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Unified Inbox</h1>
          <p className="text-gray-500 mt-1">
            Manage engagement across all connected platforms
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4 mb-4">
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-2xl font-bold text-gray-900">{stats.total_unread}</div>
          <div className="text-sm text-gray-500">Unread</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-2xl font-bold text-orange-600">{stats.needs_response_count}</div>
          <div className="text-sm text-gray-500">Need Response</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-2xl font-bold text-red-600">{stats.urgent_count}</div>
          <div className="text-sm text-gray-500">Urgent</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-2xl font-bold text-green-600">{stats.response_rate}%</div>
          <div className="text-sm text-gray-500">Response Rate</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-2xl font-bold text-gray-700">{stats.avg_response_time_hours}h</div>
          <div className="text-sm text-gray-500">Avg. Response Time</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-t-lg border border-b-0 border-gray-200 p-3 flex items-center gap-3">
        {/* Select all */}
        <input
          type="checkbox"
          checked={selectedIds.size === items.length && items.length > 0}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />

        {/* Bulk actions */}
        {selectedIds.size > 0 && (
          <>
            <span className="text-sm text-gray-500">{selectedIds.size} selected</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleBulkAction('mark_read')}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                title="Mark as read"
              >
                <Eye className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleBulkAction('mark_unread')}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                title="Mark as unread"
              >
                <EyeOff className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleBulkAction('archive')}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                title="Archive"
              >
                <Archive className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleBulkAction('flag')}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                title="Flag"
              >
                <Flag className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleBulkAction('spam')}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                title="Mark as spam"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </>
        )}

        <div className="flex-1" />

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search messages..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Filter toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`p-2 rounded-lg border ${
            showFilters ? 'border-primary-500 bg-primary-50 text-primary-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Filter className="w-4 h-4" />
        </button>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="bg-gray-50 border-x border-gray-200 p-3 flex items-center gap-4">
          <div>
            <label htmlFor="inbox-status-filter" className="text-xs text-gray-500 block mb-1">Status</label>
            <select
              id="inbox-status-filter"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">All</option>
              <option value="unread">Unread</option>
              <option value="read">Read</option>
              <option value="replied">Replied</option>
              <option value="flagged">Flagged</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div>
            <label htmlFor="inbox-priority-filter" className="text-xs text-gray-500 block mb-1">Priority</label>
            <select
              id="inbox-priority-filter"
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">All</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <label htmlFor="inbox-sentiment-filter" className="text-xs text-gray-500 block mb-1">Sentiment</label>
            <select
              id="inbox-sentiment-filter"
              value={filters.sentiment}
              onChange={(e) => setFilters({ ...filters, sentiment: e.target.value })}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">All</option>
              <option value="positive">Positive</option>
              <option value="negative">Negative</option>
              <option value="neutral">Neutral</option>
              <option value="mixed">Mixed</option>
            </select>
          </div>
          <div>
            <label htmlFor="inbox-type-filter" className="text-xs text-gray-500 block mb-1">Type</label>
            <select
              id="inbox-type-filter"
              value={filters.item_type}
              onChange={(e) => setFilters({ ...filters, item_type: e.target.value })}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">All</option>
              <option value="comment">Comments</option>
              <option value="dm">DMs</option>
              <option value="mention">Mentions</option>
              <option value="reply">Replies</option>
            </select>
          </div>
          <button
            onClick={() => setFilters({ status: '', priority: '', sentiment: '', item_type: '', search: '' })}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex bg-white border border-gray-200 rounded-b-lg overflow-hidden">
        {/* List */}
        <div className={`overflow-y-auto ${selectedItem ? 'w-1/2' : 'w-full'}`}>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <MessageSquare className="w-12 h-12 text-gray-300 mb-3" />
              <p>No messages found</p>
              <p className="text-sm text-gray-400">Try adjusting your filters</p>
            </div>
          ) : (
            items.map((item) => (
              <InboxItemRow
                key={item.id}
                item={item}
                isSelected={selectedIds.has(item.id)}
                onSelect={handleSelectItem}
                onClick={setSelectedItem}
              />
            ))
          )}
        </div>

        {/* Detail panel */}
        {selectedItem && (
          <div className="w-1/2">
            <InboxDetailPanel
              item={selectedItem}
              onClose={() => setSelectedItem(null)}
              onReply={handleReply}
              onAnalyze={handleAnalyze}
            />
          </div>
        )}
      </div>
    </div>
  );
}
